import { selectActiveTenant, useAuthStore } from '@/stores/auth-store';
import type { Role } from '@/types/domain';

/**
 * Acceso al negocio activo desde una pantalla de módulo.
 *
 * Todas las pantallas de módulo necesitan lo mismo: el `tenantId` (prefijo de las query keys
 * y de las rutas `/api/tenants/:tenantId/...`) y si el usuario puede EDITAR
 * (`role === 'ADMIN'`, espejo de `requireRole('ADMIN')` del backend). Tenerlo en un solo
 * hook evita repetir la lectura del store en cada pestaña y mantiene una única definición de
 * "puede editar".
 *
 * El rol es el del negocio activo, no global: un mismo usuario puede ser ADMIN en un negocio
 * y AGENTE_HUMANO en otro (ver `selectActiveRole` en el store).
 */

export interface ActiveTenantAccess {
  /** `null` solo si el usuario no tiene ningún negocio (caso defensivo). */
  tenantId: string | null;
  role: Role | null;
  /** Nombre del negocio activo: lo usan los títulos y el nombre de archivo de las descargas. */
  nombreEmpresa: string | null;
  /** ¿Puede guardar cambios en este negocio? (ADMIN del tenant activo). */
  canEdit: boolean;
}

export function useActiveTenantAccess(): ActiveTenantAccess {
  const tenant = useAuthStore(selectActiveTenant);

  return {
    tenantId: tenant?.id ?? null,
    role: tenant?.role ?? null,
    nombreEmpresa: tenant?.nombreEmpresa ?? null,
    canEdit: tenant?.role === 'ADMIN',
  };
}
